import React, { useRef, useEffect } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { IRecipe } from '../../api/IRecipe';
import { useAIRecipe } from '../../hooks/useAIRecipe';
import TextInput from './TextInput';
import ChatMessage from './ChatMessage';

interface RecipeAssistantProps {
  onProposal: (proposal: Partial<IRecipe>) => void;
  getCurrentRecipe?: () => Partial<IRecipe> | undefined;
}

const RecipeAssistant: React.FC<RecipeAssistantProps> = ({ onProposal, getCurrentRecipe }) => {
  const { t } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { submit, refine, reset, isLoading, error, proposal, messages } = useAIRecipe();

  const hasProposal = proposal !== null;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (proposal) {
      onProposal(proposal);
    }
  }, [proposal, onProposal]);

  const handleSubmit = async (text: string) => {
    if (hasProposal && getCurrentRecipe) {
      const currentValues = getCurrentRecipe();
      if (currentValues) {
        await refine(currentValues, text);
        return;
      }
    }
    await submit(text);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flexGrow: 1, overflow: 'auto', mb: 2 }}>
        {messages.length === 0 && !isLoading && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {t('aiAssistant.textPlaceholder')}
            </Typography>
          </Box>
        )}

        {messages.map((msg, i) => (
          <ChatMessage key={i} role={msg.role} content={msg.content} />
        ))}

        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              {t('aiAssistant.loading')}
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={reset} sx={{ mt: 1 }}>
              {t('aiAssistant.retry')}
            </Button>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      <TextInput
        onSubmit={handleSubmit}
        placeholder={
          hasProposal ? t('aiAssistant.refinePlaceholder') : t('aiAssistant.textPlaceholder')
        }
        disabled={isLoading}
        autoFocus={!hasProposal}
      />
    </Box>
  );
};

export default RecipeAssistant;
